import { sleep } from '@last/core'

import HttpFactory from '@/drivers/http'

import type { CashMachineOptions, ICashMachine } from './types'

class Cashdro implements ICashMachine {
  private static readonly OPERATIONS = {
    CHARGE: 4,
    PAY: 3
  }

  private ip!: string
  private name!: string
  private password!: string
  private inputAmountListener!: (amount: number) => void
  private totalAmountListener!: (amount: number) => void
  private http: any
  private protocol: 'https' | 'http' = 'https'
  private currentOperationId: string | null = null

  constructor() {
    this.http = HttpFactory.getHttp()
  }

  init(config: CashMachineOptions): void {
    if (config.ip && config.name && config.password) {
      this.ip = config.ip
      this.name = config.name
      this.password = config.password
      this.inputAmountListener = config.inputAmountListener
      this.totalAmountListener = config.totalAmountListener
      this.setHttpProtocol()
    }
  }

  async setHttpProtocol(): Promise<void> {
    try {
      const response = await this.http.get(`https://${this.ip}/Cashdro3WS/index.php`, undefined, {
        operation: 'getAlerts'
      })
      if (response.data.code !== 1) {
        throw new Error("Can't check Cashdro alerts")
      }
      this.protocol = 'https'
    } catch {
      this.protocol = 'http'
    }
  }

  async startOperation(amount: number, type: number): Promise<string> {
    const response = await this.http.get(
      `${this.protocol}://${this.ip}/Cashdro3WS/index.php`,
      undefined,
      {
        operation: 'startOperation',
        name: this.name,
        password: this.password,
        type,
        posid: 'lastpos',
        posuser: 'lastpos',
        parameters: JSON.stringify({ amount })
      }
    )
    if (response.data.code !== 1) {
      throw new Error(`Cashdro error: [${response.data.code}] ${response.data.data}`)
    }
    return response.data.data
  }

  async runOperation(operationId: string): Promise<string> {
    const response = await this.http.get(
      `${this.protocol}://${this.ip}/Cashdro3WS/index.php`,
      undefined,
      {
        operation: 'acknowledgeOperationId',
        name: this.name,
        password: this.password,
        operationId
      }
    )
    if (response.data.code !== 1) {
      throw new Error(`Cashdro error: [${response.data.code}] ${response.data.data}`)
    }
    return response.data.data
  }

  async checkOperation(
    operationId: string
  ): Promise<{ operationFinished: boolean; total: number }> {
    const response = await this.http.get(
      `${this.protocol}://${this.ip}/Cashdro3WS/index.php`,
      undefined,
      {
        operation: 'askOperation',
        name: this.name,
        password: this.password,
        operationId
      }
    )
    const data = JSON.parse(response.data.data)
    this.inputAmountListener(parseInt(data.operation.totalin))
    return {
      operationFinished: data.operation.state === 'F',
      total: parseInt(data.operation.totalin) + parseInt(data.operation.totalout)
    }
  }

  async cancelOperation(operationId: string): Promise<void> {
    const cancelType = 3
    await this.http.get(`${this.protocol}://${this.ip}/Cashdro3WS/index.php`, undefined, {
      operation: 'finishOperation',
      name: this.name,
      password: this.password,
      operationId,
      type: cancelType
    })
  }

  async importOperation(operationId: string): Promise<void> {
    const response = await this.http.get(
      `${this.protocol}://${this.ip}/Cashdro3WS/index.php`,
      undefined,
      {
        operation: 'setOperationImported',
        name: this.name,
        password: this.password,
        operationId
      }
    )
    if (response.data.code !== 1) {
      throw new Error(`Cashdro error: [${response.data.code}] ${response.data.data}`)
    }
  }

  private async runOperationInternal(amount: number, type: number): Promise<number> {
    if (!this.ip || !this.name || !this.password) {
      throw new Error('Cashdro not initialized')
    }
    const operationId = await this.startOperation(amount, type)
    this.currentOperationId = operationId
    await this.runOperation(operationId)
    let finished = false
    let operationAmount = 0
    while (!finished) {
      const { operationFinished, total } = await this.checkOperation(operationId)
      operationAmount = total
      finished = operationFinished
      await sleep(500)
    }
    await this.importOperation(operationId)
    return operationAmount
  }

  async charge(amount: number): Promise<number> {
    return await this.runOperationInternal(amount, Cashdro.OPERATIONS.CHARGE)
  }

  async payOut(amount: number): Promise<number> {
    return await this.runOperationInternal(amount, Cashdro.OPERATIONS.PAY)
  }

  async payIn(amount: number): Promise<number> {
    return await this.runOperationInternal(amount, Cashdro.OPERATIONS.CHARGE)
  }

  async cancel(): Promise<void> {
    if (this.currentOperationId) {
      await this.cancelOperation(this.currentOperationId)
    }
  }
}

export default new Cashdro()
